import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { fetchConfig, fetchProduct } from '@/lib/api';

const AppContext = createContext(null);

const CART_KEY = 'bb_cart_v1';
const RECENT_KEY = 'bb_recent_v1';

const loadCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};
const saveCart = (items) => {
  try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch {}
};
const loadRecent = () => {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch { return []; }
};
const saveRecent = (xs) => {
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(xs)); } catch {}
};

const cartLineKey = (item) => {
  const c = item.customization;
  const p = item.pack;
  const packSig = p?.selections
    ? Object.entries(p.selections).filter(([, v]) => v).map(([k, v]) => `${k}:${v}`).join('|')
    : '';
  if (!c && !packSig) return `${item.product_id}__plain__${item.variant || ''}`;
  // unique per customization signature
  const cSig = c
    ? [c.mode, c.preset_id || '', (c.text || '').slice(0, 20), c.text_color || '', c.background_color || '', c.upload_data_url ? 'upload' : ''].join('|')
    : '';
  return `${item.product_id}__cust__${cSig}__pack__${packSig}__${item.variant || ''}`;
};

export const AppProvider = ({ children }) => {
  const [config, setConfig] = useState({
    currency: 'INR',
    currency_symbol: '₹',
    free_shipping_threshold: 499,
    shipping_fee: 49,
    whatsapp_number: '919321450920',
    instagram_handle: 'bigbashconfectioneries',
    support_email: 'hello@bigbash.fun',
    brand: 'Big Bash',
  });
  const [cart, setCart] = useState(() => loadCart());
  const [recent, setRecent] = useState(() => loadRecent());
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    fetchConfig().then(setConfig).catch(() => {});
  }, []);

  // One-shot cart hydration: refresh name/price/image/pack_label from the API
  // so stale localStorage entries (added before naming/pricing migrations) heal
  // themselves on the first mount after an update. Blocks purchase of any item
  // that has since been marked coming_soon or removed.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    if (!cart || cart.length === 0) { hydratedRef.current = true; return; }
    hydratedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const uniqueSlugs = Array.from(new Set(cart.map((c) => c.product_slug).filter(Boolean)));
        const fetched = await Promise.all(
          uniqueSlugs.map((s) => fetchProduct(s).catch(() => null))
        );
        const bySlug = Object.fromEntries(
          uniqueSlugs.map((s, i) => [s, fetched[i]]).filter(([, v]) => v)
        );
        if (cancelled) return;
        setCart((prev) => {
          let dirty = false;
          const filtered = [];
          for (const item of prev) {
            const p = bySlug[item.product_slug];
            if (!p) { filtered.push(item); continue; }
            // Silently drop items now blocked (coming_soon) — user will see the
            // updated cart total; toast is intentionally skipped to avoid noise.
            if (p.coming_soon) { dirty = true; continue; }
            const next = {
              ...item,
              name: p.name ?? item.name,
              price: typeof p.price === 'number' ? p.price : item.price,
              image: p.image ?? item.image,
              pack_label: p.pack_label ?? item.pack_label,
              shopify_variant_id: p.shopify_variant_id ?? item.shopify_variant_id,
              shopify_customisable_variant_id: p.shopify_customisable_variant_id ?? item.shopify_customisable_variant_id,
            };
            if (
              next.name !== item.name ||
              next.price !== item.price ||
              next.image !== item.image ||
              next.pack_label !== item.pack_label
            ) {
              dirty = true;
            }
            filtered.push(next);
          }
          return dirty ? filtered : prev;
        });
      } catch { /* offline / cold start — leave cart untouched */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  const addToCart = useCallback((item) => {
    setCart((prev) => {
      const key = cartLineKey(item);
      const idx = prev.findIndex((p) => cartLineKey(p) === key);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], qty: next[idx].qty + (item.qty || 1) };
        return next;
      }
      return [...prev, { ...item, qty: item.qty || 1, line_id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}` }];
    });
  }, []);

  const updateQty = useCallback((line_id, qty) => {
    setCart((prev) => prev.map((p) => (p.line_id === line_id ? { ...p, qty: Math.max(1, qty) } : p)));
  }, []);

  const removeItem = useCallback((line_id) => {
    setCart((prev) => prev.filter((p) => p.line_id !== line_id));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = useMemo(() => cart.reduce((s, p) => s + (p.qty || 1), 0), [cart]);
  const subtotal = useMemo(() => cart.reduce((s, p) => s + p.price * p.qty, 0), [cart]);
  const shipping = subtotal >= config.free_shipping_threshold ? 0 : (subtotal === 0 ? 0 : config.shipping_fee);
  const total = subtotal + shipping;

  const pushRecent = useCallback((product) => {
    if (!product?.slug) return;
    setRecent((prev) => {
      const without = prev.filter((p) => p.slug !== product.slug);
      const next = [{ slug: product.slug, name: product.name, image: product.image, price: product.price }, ...without].slice(0, 8);
      saveRecent(next);
      return next;
    });
  }, []);

  const value = {
    config,
    cart,
    cartCount,
    subtotal,
    shipping,
    total,
    addToCart,
    updateQty,
    removeItem,
    clearCart,
    recent,
    pushRecent,
    searchOpen,
    setSearchOpen,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
